"use strict";

/**
 * Simple debounce helper (your original code expects a global debounce()).
 */
function debounce(fn, wait = 300) {
  let t;
  return function debounced(...args) {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

/**
 * Make the country selector click work on ALL pages + after section re-renders.
 * We bind ONCE globally using delegation.
 */
(function bindCountrySelectorDelegationOnce() {
  if (window.__schedoniCountrySelectorDelegated) return;
  window.__schedoniCountrySelectorDelegated = true;

  document.addEventListener("click", function (e) {
    const trigger = e.target.closest(".js-header__open-country-selector");
    if (!trigger) return;

    if (window.PubSub && typeof window.PubSub.publish === "function") {
      window.PubSub.publish("open-country-selector");
    }
  });
})();

/**
 * <s-header> custom element
 */
if (!customElements.get("s-header")) {
  class SHeader extends HTMLElement {
    constructor() {
      super();

      // Bind methods once (so add/removeEventListener works correctly)
      this.showMegaMenu = this.showMegaMenu.bind(this);
      this.hideOverlay = this.hideOverlay.bind(this);
      this.hideAllMegaMenus = this.hideAllMegaMenus.bind(this);
      this.openCartDrawer = this.openCartDrawer.bind(this);
      this.openAuthModal = this.openAuthModal.bind(this);
      this.enableCartButton = this.enableCartButton.bind(this);
      this.updateCartCounter = this.updateCartCounter.bind(this);
      this.openMobileMenu = this.openMobileMenu.bind(this);
      this.closeMobileMenu = this.closeMobileMenu.bind(this);
      this.handleWindowResize = this.handleWindowResize.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.showLoadAfter = this.showLoadAfter.bind(this);

      this.debounceResize = debounce(this.handleWindowResize, 300);
    }

    connectedCallback() {
      this.container = this.querySelector(".js-header__container");
      this.burgerButton = this.querySelector(".js-header__icons-burger");
      this.cartIcon = this.querySelector(".js-header__icons-cart");
      this.cartCounter = this.querySelector(".js-header__cart-counter");
      this.accountButton = this.querySelector(".js-header__icons-account");
      this.menuLinks = this.querySelectorAll(".js-header__links-item");
      this.megaMenus = this.querySelectorAll(".js-header__mega-menu");
      this.overlay = this.querySelector(".js-header__overlay");

      // NOTE:
      // We intentionally do NOT bind click directly to .js-header__open-country-selector here.
      // That is handled globally via delegation (works across PLP/PDP + re-renders).

      if (this.burgerButton) {
        this.burgerButton.addEventListener("click", this.openMobileMenu);
      }

      if (this.cartIcon) {
        this.cartIcon.addEventListener("click", this.openCartDrawer);
      }

      if (this.accountButton) {
        this.accountButton.addEventListener("click", this.openAuthModal);
      }

      // PubSub subscriptions
      this.updateCartCounterUnsubscriber =
        window.PubSub && typeof window.PubSub.subscribe === "function"
          ? window.PubSub.subscribe("update-cart-counter", (count) =>
              this.updateCartCounter(count)
            )
          : null;

      this.enableCartButtonUnsubscriber =
        window.PubSub && typeof window.PubSub.subscribe === "function"
          ? window.PubSub.subscribe("enable-cart-button", this.enableCartButton)
          : null;

      // Overlay + mega menu hover
      if (this.overlay) {
        this.overlay.addEventListener("mouseover", this.hideOverlay);
      }

      if (this.menuLinks && this.menuLinks.length) {
        this.menuLinks.forEach((link) =>
          link.addEventListener("mouseover", this.showMegaMenu)
        );
      }

      // Resize handling
      window.addEventListener("resize", this.debounceResize);

      // Scroll handling (make it safe on pages without hero banner)
      this.enableScroll = this.dataset.enableScroll === "true";
      if (this.enableScroll) {
        window.addEventListener("scroll", this.handleScroll);
        this.handleScroll();
      }

      // Load-after triggers
      document.addEventListener("scroll", this.showLoadAfter, { once: true });
      document.addEventListener("touchstart", this.showLoadAfter, { once: true });
      document.addEventListener("mousemove", this.showLoadAfter, { once: true });
    }

    disconnectedCallback() {
      if (typeof this.updateCartCounterUnsubscriber === "function") {
        this.updateCartCounterUnsubscriber();
      }
      if (typeof this.enableCartButtonUnsubscriber === "function") {
        this.enableCartButtonUnsubscriber();
      }

      if (this.burgerButton) {
        this.burgerButton.removeEventListener("click", this.openMobileMenu);
      }

      if (this.cartIcon) {
        this.cartIcon.removeEventListener("click", this.openCartDrawer);
      }

      if (this.accountButton) {
        this.accountButton.removeEventListener("click", this.openAuthModal);
      }

      if (this.overlay) {
        this.overlay.removeEventListener("mouseover", this.hideOverlay);
      }

      if (this.menuLinks && this.menuLinks.length) {
        this.menuLinks.forEach((link) =>
          link.removeEventListener("mouseover", this.showMegaMenu)
        );
      }

      window.removeEventListener("resize", this.debounceResize);

      if (this.enableScroll) {
        window.removeEventListener("scroll", this.handleScroll);
      }

      document.removeEventListener("scroll", this.showLoadAfter, { once: true });
      document.removeEventListener("touchstart", this.showLoadAfter, {
        once: true,
      });
      document.removeEventListener("mousemove", this.showLoadAfter, { once: true });
    }

    showMegaMenu(e) {
      const item = e.target.closest(".js-header__links-item");
      if (!item) return;

      this.hideOverlay();

      if (item.classList.contains("has-menu")) {
        const menu = item.nextElementSibling;
        if (!menu) return;

        if (this.overlay) this.overlay.classList.add("active");
        menu.classList.add("active");
        if (this.container) this.container.classList.add("active");
      }
    }

    hideAllMegaMenus() {
      if (!this.megaMenus) return;
      this.megaMenus.forEach((m) => m.classList.remove("active"));
    }

    hideOverlay() {
      this.hideAllMegaMenus();
      if (this.overlay) this.overlay.classList.remove("active");
      if (this.container) this.container.classList.remove("active");
    }

    openCartDrawer() {
      if (this.cartIcon) this.cartIcon.disabled = true;
      if (window.PubSub && typeof window.PubSub.publish === "function") {
        window.PubSub.publish("open-cart-drawer");
      }
    }

    openAuthModal() {
      if (window.PubSub && typeof window.PubSub.publish === "function") {
        window.PubSub.publish("open-login-modal");
      }
    }

    enableCartButton() {
      if (this.cartIcon) this.cartIcon.disabled = false;
    }

    updateCartCounter(count) {
      if (!this.cartCounter) return;

      if (count == 0) this.cartCounter.classList.add("is-hidden");
      else this.cartCounter.classList.remove("is-hidden");

      this.cartCounter.textContent = count;
    }

    openMobileMenu() {
      if (window.PubSub && typeof window.PubSub.publish === "function") {
        window.PubSub.publish("open-mobile-menu");
      }
    }

    closeMobileMenu() {
      if (window.PubSub && typeof window.PubSub.publish === "function") {
        window.PubSub.publish("close-mobile-menu");
      }
    }

    handleWindowResize() {
      if (window.innerWidth >= 768) this.closeMobileMenu();
      else this.hideOverlay();
    }

    handleScroll() {
      // Safe check: some templates won't have a hero banner
      const hero = document.querySelector(".js-hero-banner");
      if (!hero) return;

      const threshold = 0.05 * hero.offsetHeight;
      const bottom = hero.getBoundingClientRect().bottom;

      if (bottom - threshold <= 0) this.classList.add("is-scrolled");
      else this.classList.remove("is-scrolled");
    }

    showLoadAfter() {
      if (window.PubSub && typeof window.PubSub.publish === "function") {
        window.PubSub.publish("show-load-after");
      }
    }
  }

  customElements.define("s-header", SHeader);
}

document.addEventListener("mouseover", function (event) {
  const column = event.target.closest(".js-mega-menu-column");
  if (!column) return;

  const megaMenu = column.closest(".js-header__mega-menu");
  if (!megaMenu) return;

  const image = megaMenu.querySelector(".js-mega-menu-feature-image");
  const caption = megaMenu.querySelector(".js-mega-menu-feature-caption");

  const newImage = column.dataset.image;
  const newCaption = column.dataset.caption;

  if (image && newImage) {
    image.src = newImage;
  }

  if (caption) {
    caption.textContent = newCaption || "";
  }
});